import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  FacilityDobryAgent, FacilityDobryAgentLabels, OpeningHoursDaysOrder,
  OpeningHoursVerificationItem,
  VerificationData,
  VerificationFacility,
  VerificationImages
} from "../../../shared/interface/facility-dobry-agent";
import {environment} from "../../../../environments/environment";
import {FacilityDobryAgentService} from "../../../shared/services/facility-dobry-agent.service";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {ConfirmDialogWithReasonComponent} from "../../../shared/components/confirm-dialog-with-reason/confirm-dialog-with-reason.component";
import {
  FacilityDobryAgentVerificationEditDialogComponent
} from "../../../shared/components/facility-dobry-agent-verification-edit-dialog/facility-dobry-agent-verification-edit-dialog.component";
import {Ofwca} from "../../../shared/interface/ofwca";
import {PartnerFacility} from "../../../shared/interface/partner-facility";


@Component({
  selector: 'app-facility-dobry-agent-verification-list',
  templateUrl: './facility-dobry-agent-verification-list.component.html',
  styleUrls: ['./facility-dobry-agent-verification-list.component.scss'],
})
export class FacilityDobryAgentVerificationListComponent {
  @Input() verifications: VerificationFacility[] = [];
  @Input() facilityDobryAgentObject: FacilityDobryAgent | null = null;
  @Input() partnerFacility: PartnerFacility | null = null;
  @Input() ofwcas: Ofwca[] = [];

  @Output() edited = new EventEmitter<VerificationFacility>();
  @Output() accepted = new EventEmitter<VerificationFacility>();
  @Output() rejected = new EventEmitter<VerificationFacility>();

  readonly openingHoursDaysOrder = OpeningHoursDaysOrder;

  displayedColumns: string[] = [
    'createdAt',
    'submittedBy',
    'status',
    'reviewedBy',
    'changes',
    'more',
  ];

  expandedVerification: VerificationFacility | null = null;
  processingVerificationUuid: string | null = null;


  constructor(
    private readonly dialog: MatDialog,
    private readonly facilityDobryAgentService: FacilityDobryAgentService,
  ) {
  }

  get pendingVerifications(): VerificationFacility[] {
    return (this.verifications || []).filter(
      verification =>
        !verification.deleted
    );
    // return (this.verifications || []).filter(
    //   verification =>
    //     !verification.deleted &&
    //     verification.verificationStatus?.key === 'pending'
    // );
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['verifications']) {
      return;
    }

    if (!this.processingVerificationUuid) {
      return;
    }

    const processingVerification = this.verifications?.find(
      verification => verification.uuid === this.processingVerificationUuid
    );


    if (
      !processingVerification ||
      processingVerification.verificationStatus?.key !== 'pending'
    ) {
      this.clearProcessing();
    }
  }

  isProcessing(verification: VerificationFacility): boolean {
    return this.processingVerificationUuid === verification.uuid;
  }

  isAnyProcessing(): boolean {
    return !!this.processingVerificationUuid;
  }

  canRunAction(verification: VerificationFacility): boolean {
    return !!verification.uuid
      && verification.verificationStatus?.key === 'pending'
      && !this.isAnyProcessing();
  }

  private startProcessing(verification: VerificationFacility): void {
    this.processingVerificationUuid = verification.uuid;
  }

  private clearProcessing(): void {
    this.processingVerificationUuid = null;
  }

  toggleDetails(verification: VerificationFacility): void {
    this.expandedVerification =
      this.expandedVerification?.uuid === verification.uuid ? null : verification;
  }

  getChangedFields(verification: VerificationFacility): string[] {
    if (!verification?.changes) {
      return [];
    }

    return Object.keys(verification.changes);
  }

  getFieldLabel(field: string): string {
    return FacilityDobryAgentLabels[field] || field;
  }

  getPreviousValue(verification: VerificationFacility, field: string): unknown {
    return verification.previous?.[field as keyof VerificationData] ?? null;
  }

  getChangedValue(verification: VerificationFacility, field: string): unknown {
    return verification.changes?.[field as keyof VerificationData] ?? null;
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Tak' : 'Nie';
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '—';
      }

      return value
        .map(item => {
          if (typeof item === 'string' || typeof item === 'number') {
            return String(item);
          }

          if (this.hasName(item)) {
            return String(item.name);
          }

          if (this.hasLocation(item)) {
            return String(item.location);
          }

          return JSON.stringify(item);
        })
        .join(', ');
    }

    if (this.hasName(value)) {
      return String(value.name);
    }

    if (this.hasOpeningHours(value)) {
      return JSON.stringify(value);
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  edit(verification: VerificationFacility): void {
    if (!this.canRunAction(verification)) {
      return;
    }

    this.startProcessing(verification);

    const dialogRef = this.dialog.open(FacilityDobryAgentVerificationEditDialogComponent, {
      width: '95vw',
      maxWidth: '95vw',
      height: '90vh',
      disableClose: true,
      data: {
        verification,
      },
    });


    dialogRef.afterClosed().subscribe(result => {
      if (!result?.saved) {
        this.clearProcessing();
        return;
      }
      this.facilityDobryAgentService.editAndAcceptVerification(verification.uuid, result.payload)
        .subscribe({
          next: () => {
            this.edited.emit(verification);
          },
          error: error => {
            console.error('Błąd podczas akceptacji zmian wizytówki DA', error);
            this.clearProcessing();
          },
        });
    });
  }


  accept(verification: VerificationFacility): void {
    if (!this.canRunAction(verification)) {
      return;
    }

    this.startProcessing(verification);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        text: 'Czy na pewno chcesz zaakceptować zgłoszone zmiany w wizytówce Dobry Agent?',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.clearProcessing();
        return;
      }

      this.facilityDobryAgentService.acceptVerification(verification.uuid)
        .subscribe({
          next: () => {

            this.accepted.emit(verification);
          },
          error: error => {
            console.error('Błąd podczas akceptacji zmian wizytówki DA', error);
            this.clearProcessing();
          },
        });
    });
  }

  reject(verification: VerificationFacility): void {
    if (!this.canRunAction(verification)) {
      return;
    }

    this.startProcessing(verification);

    const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
      width: '500px',
      disableClose: true,
      data: {
        text: 'Podaj powód odrzucenia zmian:',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.accepted) {
        this.clearProcessing();
        return;
      }

      this.facilityDobryAgentService.rejectVerification(
        verification.uuid,
        result.reason
      ).subscribe({
        next: () => {

          this.rejected.emit(verification);
        },
        error: error => {
          console.error('Błąd podczas odrzucenia zmian wizytówki DA', error);
          this.clearProcessing();
        },
      });
    });
  }

  private hasName(value: unknown): value is { name: unknown } {
    return typeof value === 'object'
      && value !== null
      && 'name' in value;
  }

  private hasLocation(value: unknown): value is { location: unknown } {
    return typeof value === 'object'
      && value !== null
      && 'location' in value;
  }

  private hasOpeningHours(value: unknown): value is { openTime?: unknown; closeTime?: unknown } {
    return typeof value === 'object'
      && value !== null
      && ('openTime' in value || 'closeTime' in value);
  }

  asImages(value: unknown): VerificationImages[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is VerificationImages => {
      return typeof item === 'object'
        && item !== null
        && 'location' in item;
    });
  }

  getImageUrl(location?: string | null): string {
    if (!location) {
      return '';
    }

    if (location.startsWith('http://') || location.startsWith('https://')) {
      return location;
    }

    return environment.dobryAgentUrl + `${location}`;
  }

  formatOpeningHours(value: unknown): OpeningHoursVerificationItem[] {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return [];
    }

    const daysOrder = [
      'poniedzialek',
      'wtorek',
      'sroda',
      'czwartek',
      'piatek',
      'sobota',
      'niedziela',
    ];

    const labels: Record<string, string> = {
      poniedzialek: 'Poniedziałek',
      wtorek: 'Wtorek',
      sroda: 'Środa',
      czwartek: 'Czwartek',
      piatek: 'Piątek',
      sobota: 'Sobota',
      niedziela: 'Niedziela',
    };

    const openHours = value as Record<string, { openTime?: string; closeTime?: string }>;

    return daysOrder
      .filter(day => !!openHours[day])
      .map(day => {
        const openTime = openHours[day]?.openTime || '';
        const closeTime = openHours[day]?.closeTime || '';

        const closed =
          openTime.toLowerCase() === 'nieczynne' ||
          closeTime.toLowerCase() === 'nieczynne' ||
          !openTime ||
          !closeTime;

        return {
          key: day,
          label: labels[day] || day,
          openTime,
          closeTime,
          closed,
        };
      });
  }
}
